import type {ReactNode} from 'react';
import {useLayoutEffect, useState} from 'react';
import {createPortal} from 'react-dom';

import {
  ANNOTATION_INDICATOR_HOST_ATTRIBUTE,
  isIndicatorHost,
  scrollerContentOffset
} from './annotationIndicatorPlacement';

interface AnnotationIndicatorHostProps {
  /** What the indicators go beside, from `resolveIndicatorSibling`. */
  sibling: Element;
  /** After the text a line indicator flags; before the supplement a top-hung one heads. */
  side: 'before' | 'after';
  /** The pane whose scrolled content the indicators' offsets are measured in. */
  pane: HTMLElement;
  children: ReactNode;
}

const createHost = (): HTMLSpanElement => {
  const host = document.createElement('span');
  host.className = 'absolute';
  host.setAttribute(ANNOTATION_INDICATOR_HOST_ATTRIBUTE, '');
  return host;
};

/** Whether `host` is already in the run of hosts on `side` of `sibling`, which several can share. */
const isBeside = (host: Element, sibling: Element, side: 'before' | 'after'): boolean => {
  const next = (node: Node) => (side === 'after' ? node.nextSibling : node.previousSibling);
  for (let node = next(sibling); node && isIndicatorHost(node); node = next(node)) {
    if (node === host) {
      return true;
    }
  }
  return false;
};

/**
 * Lays `host` over the top of `pane`'s scrolled content, measured from the corner of
 * whatever box it positions against, so what it holds lands as it would in the pane.
 */
const overlayPane = (host: HTMLElement, pane: HTMLElement): void => {
  host.style.top = '0px';
  host.style.left = '0px';
  const origin = host.getBoundingClientRect();
  const paneRect = pane.getBoundingClientRect();
  host.style.top = `${-(origin.top + scrollerContentOffset(pane))}px`;
  host.style.left = `${paneRect.left + pane.clientLeft - pane.scrollLeft - origin.left}px`;
  host.style.width = `${pane.clientWidth}px`;
};

/**
 * Portals margin indicators into the DOM beside the text they flag, so the keyboard
 * and screen readers reach each one where it reads, not after the whole pane. However
 * deep that is, it lays itself over the pane, so the indicators still sit in its margin.
 * It moves only once it is no longer beside its sibling, since a move takes focus from
 * inside it.
 */
export const AnnotationIndicatorHost = ({
  sibling,
  side,
  pane,
  children
}: AnnotationIndicatorHostProps) => {
  const [host] = useState(createHost);

  useLayoutEffect(() => {
    if (!isBeside(host, sibling, side)) {
      if (side === 'after') {
        sibling.after(host);
      } else {
        sibling.before(host);
      }
    }
    overlayPane(host, pane);
  });

  useLayoutEffect(() => () => host.remove(), [host]);

  return createPortal(children, host);
};
